import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Button, Avatar, Dropdown } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  TeamOutlined,
  BankOutlined,
  KeyOutlined,
  HistoryOutlined,
  AuditOutlined,
  ScheduleOutlined,
  UserOutlined,
  BellOutlined,
  LogoutOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useColorMode } from '../../contexts/ThemeContext';
import { useProfile } from '../../contexts/ProfileContext';

const { Header, Sider, Content } = AntLayout;

// Define theme colors
const themeColors = {
  light: {
    primary: '#1890ff',
    siderBg: '#001529',
    headerBg: '#fff',
    headerText: '#000',
    contentBg: '#fff',
  },
  dark: {
    primary: '#1890ff',
    siderBg: '#001529',
    headerBg: '#141414',
    headerText: '#fff',
    contentBg: '#141414',
  }
};

// Default colors as fallback
const defaultColors = themeColors.light;

const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode = 'light' } = useColorMode() || {};
  const { profileData, fetchProfile } = useProfile();
  const username = localStorage.getItem('username');
  const role = localStorage.getItem('role');

  console.log('🏗️ Layout Debug:', {
    currentPath: location.pathname,
    role,
    username
  });

  // Use theme colors with fallback to default
  const colors = themeColors[colorMode] || defaultColors;

  // Fetch user profile on component mount
  useEffect(() => {
    if (username && !profileData) {
      fetchProfile().catch(error => {
        console.error('Failed to fetch user profile:', error);
      });
    }
  }, [username, profileData, fetchProfile]);

  const handleLogout = () => {
    console.log('🚪 Logging out');
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    navigate('/login');
  };

  const userDropdownItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }) => {
    console.log('👤 User menu clicked:', key);
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'profile') {
      navigate('/profile');
    }
  };

  const handleMenuClick = ({ key }) => {
    console.log('📋 Menu clicked:', key);
    navigate(key);
  };

  // Common menu items for all users
  const commonMenuItems = [
    {
      key: role === 'admin' ? '/' : '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: 'My Profile',
    },
    {
      key: '/notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
    },
  ];

  // User-specific menu items
  const userMenuItems = [
    {
      key: '/request-access',
      icon: <KeyOutlined />,
      label: 'Request Access',
    },
    {
      key: '/access',
      icon: <KeyOutlined />,
      label: 'Access History',
    },
    {
      key: '/schedule',
      icon: <CalendarOutlined />,
      label: 'My Schedule',
    },
  ];

  // Admin-only menu items
  const adminMenuItems = [
    {
      key: '/users',
      icon: <TeamOutlined />,
      label: 'Users',
    },
    {
      key: '/departments',
      icon: <BankOutlined />,
      label: 'Departments',
    },
    {
      key: '/access-requests',
      icon: <KeyOutlined />,
      label: 'Access Requests',
    },
    {
      key: '/schedules',
      icon: <ScheduleOutlined />,
      label: 'Schedules',
    },
    {
      key: '/access-logs',
      icon: <HistoryOutlined />,
      label: 'Access Logs',
    },
    {
      key: '/audit-logs',
      icon: <AuditOutlined />,
      label: 'Audit Logs',
    },
  ];

  const menuItems = role === 'admin' 
    ? [
        // Dashboard first for admin
        {
          key: '/',
          icon: <DashboardOutlined />,
          label: 'Dashboard',
        },
        ...adminMenuItems,
        // Other common items
        {
          key: '/profile',
          icon: <UserOutlined />,
          label: 'My Profile',
        },
        {
          key: '/notifications',
          icon: <BellOutlined />,
          label: 'Notifications',
        },
      ]
    : [...commonMenuItems, ...userMenuItems];

  console.log('📋 Menu items for role', role, ':', menuItems.map(item => item.key));

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        style={{ 
          background: colors.siderBg,
        }}
      >
        <div style={{ 
          height: 64, 
          margin: '16px',
          background: 'rgba(255, 255, 255, 0.1)', 
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff'
        }}>
          <SafetyCertificateOutlined style={{ fontSize: 24, marginRight: collapsed ? 0 : 8, color: '#ffffff' }} />
          {!collapsed && <span style={{ fontSize: 18, fontWeight: 500, color: '#ffffff' }}>Facial Access</span>}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            background: colors.siderBg,
          }}
        />
      </Sider>
      <AntLayout>
        <Header style={{ 
          padding: '0 16px', 
          background: colors.headerBg,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
          borderBottom: `1px solid ${colors.headerBorder}`
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: '16px', width: 64, height: 64, color: colors.headerText }}
          />
          <Dropdown
            menu={{
              items: userDropdownItems,
              onClick: handleUserMenuClick,
            }}
            placement="bottomRight"
          >
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#666' }}>{username}</span>
              <Avatar 
                style={{ 
                  backgroundColor: profileData?.profile_image ? 'transparent' : colors.primary,
                  marginLeft: 8 
                }} 
                src={profileData?.profile_image}
                icon={!profileData?.profile_image && <UserOutlined />} 
              />
            </div>
          </Dropdown>
        </Header>
        <Content style={{ 
          margin: '24px 16px', 
          padding: 24, 
          minHeight: 280, 
          background: colors.contentBg,
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
        }}>
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
